import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useHtmlTemplateEngine, type HtmlTemplate, type TemplateProduct } from './useHtmlTemplateEngine';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export interface SimplePrintJob {
  printerId: number;
  htmlContent: string;
  title?: string;
  copies?: number;
}

export const useSimplifiedPrinting = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const { generateHtmlLabel } = useHtmlTemplateEngine();

  const generatePdfClientSide = async (htmlContent: string, template?: HtmlTemplate): Promise<string> => {
    try {
      console.log('🔄 Generating PDF client-side as fallback...');
      
      // Create a temporary div to render the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '-9999px';
      
      // Get dimensions from template or use defaults
      const widthPx = template?.dimensions ? 
        (template.dimensions.unit === 'in' ? template.dimensions.width * 96 : template.dimensions.width) : 
        234; // 2.44in * 96dpi
      const heightPx = template?.dimensions ? 
        (template.dimensions.unit === 'in' ? template.dimensions.height * 96 : template.dimensions.height) : 
        109; // 1.14in * 96dpi
      
      tempDiv.style.width = `${widthPx}px`;
      tempDiv.style.height = `${heightPx}px`;
      
      document.body.appendChild(tempDiv);
      
      try {
        // Convert HTML to canvas
        const canvas = await html2canvas(tempDiv, {
          width: widthPx,
          height: heightPx,
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff'
        });
        
        // Create PDF from canvas
        const pdf = new jsPDF({
          orientation: widthPx > heightPx ? 'landscape' : 'portrait',
          unit: 'px',
          format: [widthPx, heightPx]
        });
        
        const imgData = canvas.toDataURL('image/png');
        pdf.addImage(imgData, 'PNG', 0, 0, widthPx, heightPx);
        
        const pdfBase64 = pdf.output('datauristring').split(',')[1];
        console.log('✅ Client-side PDF generated successfully');
        return pdfBase64;
      } finally {
        document.body.removeChild(tempDiv);
      }
    } catch (err) {
      console.error('❌ Client-side PDF generation failed:', err);
      throw new Error(`Client-side PDF generation failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const generatePdf = async (htmlContent: string, template?: HtmlTemplate): Promise<string> => {
    try {
      console.log('🔄 Generating PDF via edge function...');
      
      // Get actual label dimensions from template
      const width = template?.dimensions ? `${template.dimensions.width}${template.dimensions.unit}` : '2.44in';
      const height = template?.dimensions ? `${template.dimensions.height}${template.dimensions.unit}` : '1.14in';
      
      console.log(`📐 Using label dimensions: ${width} x ${height}`);
      
      const { data, error } = await supabase.functions.invoke('html-to-pdf', {
        body: { 
          html: htmlContent,
          options: {
            width,
            height,
            margin: { top: 0, right: 0, bottom: 0, left: 0 },
            printBackground: true,
            scale: 1
          }
        }
      });

      if (error) throw error;

      if (data.success) {
        console.log('✅ PDF generated successfully');
        console.log(`📏 PDF dimensions: ${data.dimensions?.width || width} x ${data.dimensions?.height || height}`);
        if (data.type === 'optimized_html') {
          console.log('ℹ️ Using optimized HTML fallback');
        }
        return data.pdf;
      } else {
        throw new Error(data.error || 'PDF generation failed');
      }
    } catch (err) {
      console.error('❌ Edge function PDF generation failed, trying client-side fallback:', err);
      // Try client-side fallback
      return await generatePdfClientSide(htmlContent, template);
    }
  };

  const printLabel = async (
    template: HtmlTemplate, 
    product: TemplateProduct, 
    printerId: number,
    format: 'html' | 'pdf' = 'pdf'
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      // Generate HTML content
      const htmlContent = generateHtmlLabel(template, product);
      console.log('📝 Generated HTML label content');

      let printContent: string;
      let contentType: 'pdf_base64' | 'raw_base64';

      if (format === 'pdf') {
        // Generate PDF from HTML with correct template dimensions
        printContent = await generatePdf(htmlContent, template);
        contentType = 'pdf_base64';
        console.log('📄 Using PDF format for printing with template dimensions');
      } else {
        // Use HTML directly with proper sizing
        const optimizedHtml = `
          <!DOCTYPE html>
          <html>
          <head>
            <meta charset="UTF-8">
            <style>
              @page {
                size: ${template.dimensions.width}${template.dimensions.unit} ${template.dimensions.height}${template.dimensions.unit};
                margin: 0;
              }
              body {
                width: ${template.dimensions.width}${template.dimensions.unit};
                height: ${template.dimensions.height}${template.dimensions.unit};
                margin: 0;
                padding: 0;
                overflow: hidden;
              }
            </style>
          </head>
          <body>
            ${htmlContent}
          </body>
          </html>
        `;
        printContent = btoa(unescape(encodeURIComponent(optimizedHtml)));
        contentType = 'raw_base64';
        console.log(`📝 Using HTML format for printing with dimensions: ${template.dimensions.width}${template.dimensions.unit} x ${template.dimensions.height}${template.dimensions.unit}`);
      }

      // Send to PrintNode
      const { data, error } = await supabase.functions.invoke('printnode-print', {
        body: {
          action: 'print',
          printerId,
          content: printContent,
          title: `Label - ${product.product_name}`,
          copies: 1,
          contentType
        }
      });

      if (error) throw error;

      if (data.success) {
        toast({
          title: "Print Successful",
          description: `Label sent to printer successfully`,
        });
        return true;
      } else {
        throw new Error(data.error || 'Print job failed');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Print job failed';
      setError(errorMessage);
      toast({
        title: "Print Failed",
        description: errorMessage,
        variant: "destructive",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const browserPrint = (template: HtmlTemplate, product: TemplateProduct) => {
    console.log('🖨️ Opening browser print dialog...');
    
    // Generate HTML content
    const htmlContent = generateHtmlLabel(template, product);
    
    // Open print window
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast({
        title: "Print Blocked",
        description: "Please allow popups for this site to use browser printing",
        variant: "destructive",
      });
      return;
    }

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Wait for content to load, then print
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 100);
    };
  };

  return {
    loading,
    error,
    printLabel,
    browserPrint,
    generatePdf
  };
};